import { ZodType, z } from 'zod';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { cardRegex } from '../../../../../constants/constant';

export function getCardType(cardNumber: string, setCardType: any) {
  const cleanedCardNumber = cardNumber.replace(/\D/g, '');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const cardType = Object.entries(cardRegex).find(([_, regex]) => regex.test(cleanedCardNumber))?.[0];
  setCardType(cardType ?? 'unknown');
}

const isValidCardType = (value: string) => {
  const cleanedValue = value.replace(/\D/g, '');
  let cardType = 'unknown';
  getCardType(cleanedValue, (type: any) => (cardType = type));
  return cardType !== 'unknown';
};

const isValidMonth = (value: string) => {
  const [monthStr] = value.split('/');
  const month = parseInt(monthStr, 10);
  return !isNaN(month) && month >= 1 && month <= 12;
};

const isExpiryDateInFuture = (value: string) => {
  const [monthStr, yearStr] = value.split('/');
  const currentDate = new Date();
  const month = parseInt(monthStr, 10);
  const year = parseInt(yearStr, 10) + 2000;
  const expiryDate = new Date(year, month, 1);
  return expiryDate > currentDate;
};

const isValidCVV = (value: string, isMailOrder?: boolean) => {
  const cleanedValue = value.replace(/\D/g, '');

  if (isMailOrder) {
    if (value.length > 0) {
      return cleanedValue.length === 3 || cleanedValue.length === 4;
    }
    return true;
  }

  return cleanedValue.length === 3 || cleanedValue.length === 4;
};

// Helper functions to support schema validations
const isNonEmptyString = (value: string) => value.trim().length > 0;

const isValidCardNumber = (value: string) => {
  const cleanedValue = value.replace(/\D/g, '');
  return cleanedValue.length === 15 || cleanedValue.length === 16;
};

export const createPaymentSchema = (params: { [key: string]: any }) => {
  const isMailOrder = params?.isMailOrder || false;
  const cartHasInsurance = params?.cartHasInsurance || false;

  const emptyEmailMessage = cartHasInsurance
    ? mapLocaleToMeaningfulFormat().emailRequiredForWorryFreeDelivery
    : mapLocaleToMeaningfulFormat().emptyEmailErrorMessage;

  const invalidEmailMessage = cartHasInsurance
    ? 'Please enter a valid email address To purchase Worry Free Delivery.'
    : 'Please enter a valid email address.';

  return z.object({
    cardNumber: z
      .string()
      .refine(isNonEmptyString, { message: 'Enter a credit card number.' })
      .refine(isValidCardNumber, { message: 'Enter a valid card number.' })
      .refine(isValidCardType, { message: 'Enter a valid card number.' })
      .optional(),
    expDate: z
      .string()
      .refine(isNonEmptyString, { message: 'Enter expiry date.' })
      .refine(isValidMonth, { message: 'Invalid month' })
      .refine(isExpiryDateInFuture, { message: 'Expiry date must be in future' })
      .optional(),
    securityCode: z
      .string()
      .refine(
        (value) => {
          return isValidCVV(value, isMailOrder);
        },
        { message: 'Enter a security code.' },
      )
      .optional(),
    firstName: z
      .string()
      .min(1, { message: mapLocaleToMeaningfulFormat().emptyFirstNameErrorMessage })
      .max(40, { message: 'Max 40 characters' })
      .regex(/^[a-zA-Z0-9][a-zA-Z0-9\s'-]*[a-zA-Z0-9]?\s*$/, { message: 'Please enter valid first name' })
      .refine((value) => value.replace(/[^0-9a-zA-Z\s'-]/g, ''), { message: 'Invalid character' })
      .transform((value) => value.trim())
      .optional(),

    lastName: z
      .string()
      .min(1, { message: mapLocaleToMeaningfulFormat().emptyLastNameErrorMessage })
      .max(40, { message: 'Max 40 characters' })
      .regex(/^[a-zA-Z0-9][a-zA-Z0-9\s'-]*[a-zA-Z0-9]?\s*$/, { message: 'Please enter valid last name' })
      .refine((value) => value.replace(/[^0-9a-zA-Z\s'-]/g, ''), { message: 'Invalid character' })
      .transform((value) => value.trim())
      .optional(),

    streetName: z
      .string()
      .min(1, { message: mapLocaleToMeaningfulFormat().emptyAddress1 })
      .max(30, { message: mapLocaleToMeaningfulFormat().addressLineMaxLength })
      .regex(/^[a-zA-Z0-9][a-zA-Z0-9\s',-]*(?:\s*[a-zA-Z0-9][a-zA-Z0-9\s',-]*)*\s*$/, {
        message: mapLocaleToMeaningfulFormat().addressMessage,
      })
      .refine((value) => value.replace(/[^0-9a-zA-Z\s',-]/g, ''), {
        message: mapLocaleToMeaningfulFormat().invalidChar,
      })
      .transform((value) => value.trim())
      .optional(),

    additionalAddressInfo: z
      .string()
      .max(30, { message: 'Maximum 30 characters are allowed' })
      .refine(
        (value) => {
          if (value) {
            return /^[a-zA-Z0-9\s/#.,;:'\-\u00B0]+$/.test(value);
          }
          return true;
        },
        { message: 'Invalid character' },
      )
      .optional(),

    city: z
      .string()
      .min(1, { message: mapLocaleToMeaningfulFormat().emptyCity })
      .max(30, { message: 'Maximum 30 characters are allowed' })
      .regex(/^[a-zA-Z\s\-,.;'&/()]+$/, { message: 'Please enter a valid city name' })
      .transform((value) => value.replace(/[^a-zA-Z\s\-,.;'&/()]/g, ''))
      .transform((value) => value.trim())
      .optional(),

    state: z.string().min(1, { message: mapLocaleToMeaningfulFormat().emptyState }).max(50).optional(),
    country: z.string().optional(),
    postalCode: z
      .string()
      .min(1, { message: mapLocaleToMeaningfulFormat().emptyzipcode })
      .refine(
        (value) => {
          const sanitizedValue = value.replace(/\D/g, '');
          const isValidLength =
            sanitizedValue.length === 5 || (sanitizedValue.length >= 9 && sanitizedValue.length <= 10);
          const isNotAllZeros = !/^0+$/.test(sanitizedValue);
          return isValidLength && isNotAllZeros;
        },
        { message: mapLocaleToMeaningfulFormat().invalidPostalCode },
      )
      .optional(),
    phone: z
      .string()
      .refine((value) => !/^0+$/.test(value.replace(/\D/g, '')), {
        message: mapLocaleToMeaningfulFormat().invalidPhoneNumberMessage,
      })
      .optional(),

    email: z.string().min(1, { message: emptyEmailMessage }).email({ message: invalidEmailMessage }).optional(),
    password: z
      .string()
      .min(8, { message: 'Please enter a valid password' })
      .regex(/\d/g, { message: 'Need a digit' })
      .regex(/[a-zA-Z]/g, {
        message: 'Need a letter',
      })
      .optional(),
    batchNumber: z.string().refine(isNonEmptyString, { message: 'Enter Batch Number' }).optional(),
    cashAmount: z.string().refine(isNonEmptyString, { message: 'Enter Cash Amount' }).optional(),
    checkNumber: z.string().refine(isNonEmptyString, { message: 'Enter Check Number' }).optional(),
    totalCheckAmount: z.string().refine(isNonEmptyString, { message: 'Enter Total Check Amount' }).optional(),
    ssn: z
      .string()
      .refine(isNonEmptyString, { message: 'Enter last 4 digits of SSN' })
      .refine(
        (value) => {
          const cleanedValue = value.replace(/\D/g, '');
          const length = cleanedValue.length;
          return length === 4;
        },
        { message: mapLocaleToMeaningfulFormat().ssnNumber },
      )
      .optional(),
    dob: z
      .string()
      .refine(
        (value) => {
          const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
          if (!dobRegex.test(value)) {
            return false;
          }

          const [month, day, year] = value.split('/').map(Number);
          const dobDate = new Date(year, month - 1, day);

          if (isNaN(dobDate.getTime())) {
            return false;
          }

          const lastDay = new Date(year, month, 0).getDate();
          if (day > lastDay) {
            return false;
          }

          const currentDate = new Date();
          return dobDate < currentDate;
        },
        { message: mapLocaleToMeaningfulFormat().validDOB },
      )
      .optional(),
    dpqDOB: z
      .string()
      .refine(isNonEmptyString, { message: 'Enter Date of Birth' })
      .refine(
        (value) => {
          const dobRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/;
          if (!dobRegex.test(value)) {
            return false;
          }

          const [month, day, year] = value.split('/').map(Number);
          const dobDate = new Date(year, month - 1, day);

          if (isNaN(dobDate.getTime())) {
            return false;
          }

          const lastDay = new Date(year, month, 0).getDate();
          if (day > lastDay) {
            return false;
          }

          const currentDate = new Date();
          return dobDate < currentDate;
        },
        { message: mapLocaleToMeaningfulFormat().validDOB },
      )
      .refine(
        (value) => {
          const [month, day, year] = value.split('/').map(Number);
          const dobDate = new Date(year, month - 1, day);
          const currentDate = new Date();

          // Calculate age
          let age = currentDate.getFullYear() - dobDate.getFullYear();
          const monthDiff = currentDate.getMonth() - dobDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && currentDate.getDate() < dobDate.getDate())) {
            age--;
          }

          return age >= 21;
        },
        { message: 'Age should be atleast 21 years' },
      )
      .optional(),
    catalogId: z.string().refine(isNonEmptyString, { message: 'Enter catalog id' }).optional(),
    acceptTerms: z
      .boolean()
      .refine(
        (val) => {
          return val === true;
        },
        {
          message: 'Please read and confirm the Terms & Conditions to proceed.',
        },
      )
      .optional(),
  });
};

export const PaymentSchema: ZodType<any> = createPaymentSchema({});
